import { ReactNode } from 'react';

import { Scroller, ScrollerProps } from '../../../../components/scroller';
import { DialogCard, DialogCardProps } from '../../components/DialogCard';
import { DialogFooter, DialogFooterProps } from '../../components/DialogFooter';
import {
  DialogFootnote,
  DialogFootnoteProps,
} from '../../components/DialogFootnote';
import { DialogHeader, DialogHeaderProps } from '../../components/DialogHeader';

export type DialogPropsV2<RESULT = unknown> = {
  /**
   * Title of the dialog, displayed at the start of the header
   */
  title: ReactNode;
  /**
   * The name or identifier of the object being affected. Shown below the title.
   * Use only for a resource name (e.g. cluster name, policy name). Nothing else.
   */
  metadata?: ReactNode;
  /**
   * Content to be displayed at the end of the header
   */
  headerEndContent?: ReactNode;
  /**
   * Should divider be displayed at the bottom of the header
   * @default false
   */
  headerDivider?: boolean;
  /**
   * Stepper, to be displayed above the dialog
   */
  stepper?: ReactNode;
  /**
   * Should divider be displayed at the top of the footer
   * @default false
   */
  footerDivider?: boolean;
  /**
   * Actions of the dialog, displayed at the start of the footer
   */
  startActions?: ReactNode;
  /**
   * Actions of the dialog, displayed at the end of the footer
   */
  endActions?: ReactNode;
  /**
   * Optional footnote content to be displayed at the bottom of the dialog
   */
  footnote?: ReactNode;
  testId?: string;
  children?: ReactNode;
  /**
   * Properties, forwarded to the underlying components
   */
  slotProps?: {
    root?: Partial<DialogCardProps>;
    header?: Partial<DialogHeaderProps>;
    scroller?: Partial<ScrollerProps>;
    footer?: Partial<DialogFooterProps>;
    footnote?: Partial<DialogFootnoteProps>;
  };
} & DialogCardProps<RESULT>;

/**
 * Standard dialog variant with mandatory title, scrollable content, optional footer, and optional footnote
 */
export const DialogV2 = ({
  title,
  metadata,
  headerEndContent,
  headerDivider = false,
  footerDivider = false,
  children,
  endActions,
  startActions,
  footnote,
  stepper,
  slotProps,
  result,
  testId,
  hideCloseButton,
}: DialogPropsV2) => {
  return (
    <DialogCard
      {...slotProps?.root}
      testId={testId}
      contentAbove={stepper}
      contentBelow={
        !!footnote && (
          <DialogFootnote {...slotProps?.footnote}>{footnote}</DialogFootnote>
        )
      }
      result={result}
      hideCloseButton={hideCloseButton}
    >
      <DialogHeader
        title={title}
        metadata={metadata}
        endContent={headerEndContent}
        divider={headerDivider}
        {...slotProps?.header}
      />
      <Scroller {...slotProps?.scroller}>{children}</Scroller>
      {(!!startActions ||
        !!endActions ||
        !!slotProps?.footer?.startContent ||
        slotProps?.footer?.endContent) && (
        <DialogFooter
          startContent={startActions}
          endContent={endActions}
          divider={footerDivider}
          {...slotProps?.footer}
        />
      )}
    </DialogCard>
  );
};
